import { type ComponentProps, type ReactNode, useEffect, useRef } from 'react'
import { Chip, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Radio, Tooltip, Typography } from './generic.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { Stack } from './Stack.tsx'

interface RadioOption<T extends string | number> {
	value: T
	label: ReactNode
	icon?: IconName
	chips?: string[]
	disabled?: boolean
	tooltip?: ReactNode
}

interface CustomRadioGroupProps<T extends string | number> extends Omit<ComponentProps<typeof List>, 'onChange'> {
	options: RadioOption<T>[]
	value?: T
	onChange: (value: T) => void
}

export const CustomRadioGroup = <T extends string | number>({ options, value, onChange, ...props }: CustomRadioGroupProps<T>) => {
	const selectedItemRef = useRef<HTMLDivElement>(null)
	const firstRender = useRef(true)

	useEffect(() => {
		if (value)
			selectedItemRef?.current?.scrollIntoView({
				behavior: firstRender.current ? 'instant' : 'smooth',
				block: firstRender.current ? 'start' : 'nearest',
			})
		firstRender.current = false
	}, [value])

	return (
		<List {...props}>
			{options.map((option) => {
				const selected = option.value === value
				return (
					<Tooltip key={option.value} title={option.tooltip} placement='top-end'>
						<ListItem
							data-ta={option.value}
							disablePadding
							slotProps={{ secondaryAction: { sx: { pointerEvents: 'none' } } }}
							secondaryAction={<Radio size='large' checked={selected} disabled={option.disabled} />}
						>
							<ListItemButton
								ref={selected ? selectedItemRef : null}
								selected={selected}
								onClick={() => onChange(option.value)}
								disabled={option.disabled}
							>
								<ListItemIcon>{option.icon && <Icon name={option.icon} color='textSecondary' />}</ListItemIcon>
								<ListItemText primary={<Typography noWrap>{option.label}</Typography>} />
								{option.chips && (
									<Stack>
										{option.chips.map((chip) => (
											<Chip data-ta={`${option.value}-${chip}`} key={chip} label={chip} variant='outlined' />
										))}
									</Stack>
								)}
							</ListItemButton>
						</ListItem>
					</Tooltip>
				)
			})}
		</List>
	)
}
